import React, { useCallback, useMemo } from 'react';
import { View, ScrollView } from 'react-native';

import { useUserContext } from '../../../common/utils/context';
import { getLocalTime } from '../../../common/utils/time/time';
import { getFullMachineName } from '../../../common/utils/machine';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Dialog, ErrorCallout, Icon, Separator, Text, TypographyHeading, WarningCallout } from '../../components';
import { useTheme } from '../../theme';

import { getPickListSummaryExpiryWarningTexts } from './pickListSummaryExpiryWarnings';

// Expiry warnings for one summary product: overall callouts plus one block per POS.
const PickListSummaryExpiryWarningsDialog = ({
	item: expiryWarningsDialogItem,
	onClose,
	serviceDockets,
	planned,
}) => {
	const { tenant, tenantSettings } = useUserContext();
	const themedStyles = useThemedStyles(createThemedStyles);
	const { theme: { ui: { colors } } } = useTheme();

	const docketsById = useMemo(() => {
		return (serviceDockets || []).reduce((acc, docket) => {
			acc[docket.id] = docket;
			return acc;
		}, {});
	}, [serviceDockets]);

	const expiryWarningsDialogDockets = useMemo(() => {
		if (!expiryWarningsDialogItem?.dockets) {
			return [];
		}

		return Object.entries(expiryWarningsDialogItem.dockets)
			.map(([docketId, docketValues]) => {
				const warnings = docketValues?.expiryWarnings;
				if (!warnings || warnings.status === 'none') {
					return null;
				}

				const docket = docketsById[docketId];
				const machineName = docket?.machine
					? getFullMachineName(docket.machine, docket.machine.place, docket.machine.location)
					: 'Unknown POS';

				return {
					docketId,
					order: docket?.order,
					machine: docket?.machine,
					machineName,
					warnings,
				};
			})
			.filter(Boolean)
			.sort((a, b) => (a.order || 0) - (b.order || 0));
	}, [expiryWarningsDialogItem, docketsById]);

	const expiryThresholdDays = tenantSettings?.expiryDateThresholds?.[0] || 7;
	const tenantTimezone = tenant?.timezone || 'Australia/Melbourne';
	const plannedServiceDate = planned ? getLocalTime(tenantTimezone, planned).startOf('day') : null;

	const expiryWarningsDialogSummaryTexts = expiryWarningsDialogItem?.expiryWarnings
		? getPickListSummaryExpiryWarningTexts({
			expiryWarnings: expiryWarningsDialogItem.expiryWarnings,
			expiryThresholdDays,
			tenantTimezone,
		})
		: null;

	const getBeforeNextServiceDetailsText = useCallback((machine, warnings) => {
		if (!warnings?.expiringBeforeNextServiceCount) {
			return null;
		}

		const rawServiceFrequencyDays = Number(machine?.averageDaysSinceLastService);
		const resolvedServiceFrequencyDays = Number.isFinite(rawServiceFrequencyDays) && rawServiceFrequencyDays > 0
			? Math.max(1, Math.round(rawServiceFrequencyDays))
			: 7;

		const currentDate = getLocalTime(tenantTimezone).startOf('day');
		const nextServiceDate = warnings.nextServiceDate ? getLocalTime(tenantTimezone, warnings.nextServiceDate).startOf('day') : null;
		const soonestExpiry = warnings.soonestExpiryBeforeNextService
			? getLocalTime(tenantTimezone, warnings.soonestExpiryBeforeNextService).startOf('day')
			: null;

		const serviceFrequencyLabel = resolvedServiceFrequencyDays === 1 ? 'day' : 'days';

		const detailsLines = [
			`Service frequency: ${resolvedServiceFrequencyDays} ${serviceFrequencyLabel}`,
			plannedServiceDate ? `Planned service: ${plannedServiceDate.format('MMM DD')}` : null,
			nextServiceDate ? `Next service: ${nextServiceDate.format('MMM DD')}` : null,
		].filter(Boolean);

		if (soonestExpiry) {
			const daysUntilSoonestExpiry = soonestExpiry.diff(currentDate, 'days');
			const daysLabel = daysUntilSoonestExpiry === 1 ? 'day' : 'days';
			detailsLines.push(`Soonest expiry: ${soonestExpiry.format('MMM DD')} (in ${daysUntilSoonestExpiry} ${daysLabel})`);
		}

		return detailsLines.join('\n');
	}, [plannedServiceDate, tenantTimezone]);

	return (
		<Dialog
			isOpen={Boolean(expiryWarningsDialogItem)}
			onClose={onClose}
		>
			<View>
				<TypographyHeading label="Expiry warnings" />
				{expiryWarningsDialogItem?.productName && (
					<Text style={themedStyles.expiryWarningsDialogProductName}>
						{expiryWarningsDialogItem.productName}
					</Text>
				)}
				{
					plannedServiceDate && (
						<Text style={themedStyles.expiryWarningsDialogSubheading}>
							{`Planned service: ${plannedServiceDate.format('MMM DD')}`}
						</Text>
					)
				}
				{
					expiryWarningsDialogSummaryTexts && (
						<View style={themedStyles.expiryWarningsDialogSummaryCallouts}>
							{expiryWarningsDialogSummaryTexts.mandatoryWarningText && (
								<ErrorCallout text={expiryWarningsDialogSummaryTexts.mandatoryWarningText} />
							)}
							{expiryWarningsDialogSummaryTexts.expiresBeforeNextServiceText && (
								<ErrorCallout
									text={expiryWarningsDialogSummaryTexts.expiresBeforeNextServiceText}
									additionalText={expiryWarningsDialogSummaryTexts.expiresBeforeNextServiceAdditionalText}
									rightTextIcon="arrowRight"
								/>
							)}
							{expiryWarningsDialogSummaryTexts.optionalWarningText && (
								<WarningCallout text={expiryWarningsDialogSummaryTexts.optionalWarningText} />
							)}
						</View>
					)
				}
				<Separator style={themedStyles.expiryWarningsDialogSeparator} />
				<TypographyHeading label="Per POS" style={themedStyles.expiryWarningsDialogSectionTitle} />
				<ScrollView
					style={themedStyles.expiryWarningsDialogMachines}
					scrollIndicatorInsets={{ right: 1 }}
				>
					{
						expiryWarningsDialogDockets.map(({ docketId, machine, machineName, warnings }) => {
							const statusColor = warnings.status === 'red' ? colors.danger : colors.warning;
							const machineWarningTexts = getPickListSummaryExpiryWarningTexts({
								expiryWarnings: warnings,
								expiryThresholdDays,
								tenantTimezone,
							});
							const beforeNextServiceDetailsText = getBeforeNextServiceDetailsText(machine, warnings);

							return (
								<View key={docketId} style={themedStyles.expiryWarningsDialogMachine}>
									<View style={themedStyles.expiryWarningsDialogMachineHeader}>
										<Icon
											name="exclamation"
											size={14}
											color={statusColor}
											style={themedStyles.expiryWarningsDialogMachineIcon}
										/>
										<Text style={themedStyles.expiryWarningsDialogMachineName}>
											{machineName}
										</Text>
									</View>
									{machineWarningTexts.mandatoryWarningText && (
										<ErrorCallout text={machineWarningTexts.mandatoryWarningText} />
									)}
									{machineWarningTexts.expiresBeforeNextServiceText && (
										<ErrorCallout
											text={machineWarningTexts.expiresBeforeNextServiceText}
											additionalText={machineWarningTexts.expiresBeforeNextServiceAdditionalText}
											rightTextIcon="arrowRight"
										/>
									)}
									{machineWarningTexts.optionalWarningText && (
										<WarningCallout text={machineWarningTexts.optionalWarningText} />
									)}
									{beforeNextServiceDetailsText && (
										<Text style={themedStyles.expiryWarningsDialogMachineDetails}>
											{beforeNextServiceDetailsText}
										</Text>
									)}
								</View>
							);
						})
					}
				</ScrollView>
			</View>
		</Dialog>
	);
};

const createThemedStyles = ({ ui: { colors, type, space } }) => ({
	expiryWarningsDialogProductName: {
		...type.body,
		marginTop: space.xs,
		color: colors.textSecondary,
	},
	expiryWarningsDialogSubheading: {
		...type.secondary,
		marginTop: space.xs,
		color: colors.textSecondary,
	},
	expiryWarningsDialogSummaryCallouts: {
		marginTop: space.md,
	},
	expiryWarningsDialogSeparator: {
		marginTop: space.md,
		marginBottom: space.md,
	},
	expiryWarningsDialogSectionTitle: {
		...type.sectionTitle,
	},
	expiryWarningsDialogMachines: {
		maxHeight: 350,
	},
	expiryWarningsDialogMachine: {
		marginTop: space.md,
	},
	expiryWarningsDialogMachineHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		marginBottom: 6,
	},
	expiryWarningsDialogMachineIcon: {
		marginRight: space.sm,
	},
	expiryWarningsDialogMachineName: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	expiryWarningsDialogMachineDetails: {
		...type.secondary,
		marginTop: space.xs,
		color: colors.textSecondary,
	},
});

export default PickListSummaryExpiryWarningsDialog;
